import { Link } from 'react-router-dom';
import { Button } from '@crazyegginc/hatch';
import { WebP } from '/src/components/WebP';
import classNames from 'classnames';

import { useMetric, useManageSubscriptionPath } from '/src/hooks';

import { ReactComponent as TickIcon } from '@crazyegginc/hatch/dist/images/icon-tick-circle-filled.svg';
export function UpsellWall({ children }) {
  return <div className="py-10">{children}</div>;
}

function UpsellWallPanel({ children, withSpacing = false }) {
  return (
    <div
      className={classNames(
        'rounded-lg bg-white border border-[#e1e6ef] shadow-[0_4px_6px_rgba(7,31,49,0.07)] w-full overflow-hidden',
        {
          'p-10': withSpacing,
          flex: !withSpacing,
        },
      )}
    >
      {children}
    </div>
  );
}

function UpsellWallTopPanel({
  textContent,
  imageWebP,
  imagePNG,
  imageAlt,
  imageWidth,
  imageHeight,
  imgWrapperClassName,
}) {
  const haveImage = imageWebP && imagePNG;

  return (
    <div className="mb-5">
      <UpsellWallPanel>
        <div className="p-10 flex-1">{textContent}</div>
        {haveImage && (
          <WebP
            webp={imageWebP}
            fallback={imagePNG}
            width={imageWidth}
            height={imageHeight}
            alt={imageAlt}
            className={classNames('flex items-center mt-auto', imgWrapperClassName)}
            imageClassName="w-full"
            style={{ maxWidth: `${imageWidth}px` }}
          />
        )}
      </UpsellWallPanel>
    </div>
  );
}

function UpsellWallBottomPanel({ isSeparate = false, leftContent, rightContent }) {
  if (isSeparate) {
    return (
      <div className="space-x-6 flex items-start">
        <UpsellWallPanel withSpacing={true}>{leftContent}</UpsellWallPanel>
        <UpsellWallPanel withSpacing={true}>{rightContent}</UpsellWallPanel>
      </div>
    );
  }
  return (
    <UpsellWallPanel withSpacing={true}>
      <div className="space-x-10 flex">
        <div className="w-full">{leftContent}</div>
        <div className="w-full">{rightContent}</div>
      </div>
    </UpsellWallPanel>
  );
}

function UpsellWallTitle({ titleText, subtitleText }) {
  return (
    <>
      <h2 className="text-xl font-semibold leading-tight text-black-pearl-500 mb-4">{titleText}</h2>
      {subtitleText && <p className="text-sm font-normal leading-tight text-black-pearl-500 mb-4">{subtitleText}</p>}
    </>
  );
}

function UpsellWallList({ list, noTextBelow = true }) {
  return (
    <ul
      className={classNames({
        'mb-6': noTextBelow,
        'mb-4': !noTextBelow,
      })}
    >
      {list.map((items) => {
        return (
          <li key={items.text} className="flex items-center mb-3 last:mb-0 text-[#0A1C2E]">
            {items.number && (
              <span className="text-2xs font-bold w-6 h-6 flex items-center justify-center rounded-full bg-[#fbbd33] text-[#883b17]">
                {items.number}
              </span>
            )}
            {!items.number && <TickIcon className="h-4 w-4 fill-current text-lima-500" />}
            <span className="ml-4 text-sm font-normal">{items.text}</span>
          </li>
        );
      })}
    </ul>
  );
}

function UpsellWallTestimonialText({ children, textClassNames }) {
  return (
    <p className={classNames('text-base font-semibold text-black-pearl-500 leading-tight', textClassNames)}>
      &ldquo;{children}&rdquo;
    </p>
  );
}

function UpsellWallCTA({ metricType, canManageBilling, ownerEmail, buttonText = 'Upgrade Now' }) {
  const trackMetric = useMetric();
  const manageSubscriptionPath = useManageSubscriptionPath();

  return (
    <div className="flex items-start flex-wrap">
      {canManageBilling ? (
        <Button
          component={Link}
          to={manageSubscriptionPath}
          onMouseDown={() => trackMetric('upgrade_cta_click', metricType)}
        >
          {buttonText}
        </Button>
      ) : (
        <Button component="a" href={`mailto:${ownerEmail}`} className="!h-[36px]">
          Contact Account Owner
        </Button>
      )}
    </div>
  );
}

function UpsellWallTestimonialPerson({
  name,
  title,
  imagePNG,
  imageWidth,
  imageHeight,
  logoWebP,
  logoPNG,
  logoWidth,
  logoHeight,
  logoAlt,
  logoSVG: Icon,
}) {
  const haveImage = imagePNG;
  const haveSVG = Icon;
  const haveWebPandPNG = logoWebP && logoPNG;
  const haveLogo = haveWebPandPNG || haveSVG;

  return (
    <div
      className={classNames({
        'flex justify-between items-center': haveLogo,
      })}
    >
      <div className="flex items-center">
        {haveImage && (
          <img className="rounded-full mr-4" src={imagePNG} alt={name} width={imageWidth} height={imageHeight} />
        )}
        <div>
          <p className="text-sm font-bold text-black-pearl-500 leading-tight">{name}</p>
          <p className="text-xs font-normal text-lynch-500 leading-tight">{title}</p>
        </div>
      </div>
      {haveWebPandPNG && (
        <WebP
          style={{ width: `${logoWidth}px`, height: `${logoHeight}px` }}
          webp={logoWebP}
          fallback={logoPNG}
          width={logoWidth}
          height={logoHeight}
          alt={logoAlt}
        />
      )}
      {haveSVG && <Icon style={{ width: `${logoWidth}px`, height: `${logoHeight}px` }} aria-label={logoAlt} />}
    </div>
  );
}

UpsellWall.TopPanel = UpsellWallTopPanel;
UpsellWall.BottomPanel = UpsellWallBottomPanel;
UpsellWall.Title = UpsellWallTitle;
UpsellWall.List = UpsellWallList;
UpsellWall.CTA = UpsellWallCTA;
UpsellWall.TestimonialText = UpsellWallTestimonialText;
UpsellWall.TestimonialPerson = UpsellWallTestimonialPerson;
